import mapboxgl from 'mapbox-gl';
import { useUnit } from 'effector-react';
import { useEffect, useRef } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import {
  $mapLoaded,
  resetMapLoadedEv,
  setMapLoadedEv,
} from '../../models/mapModel/index.js';
import {
  createObject,
  customStylesLight,
  deleteAll,
  ExtendDrawBar,
  modeChangeHandler,
  updatePolygon,
} from '../../utils/draw_polygon_utils.js';

mapboxgl.accessToken =
  'pk.eyJ1IjoiZnJvbnRpZXJkIiwiYSI6ImNrYTNuMjBvNjAxNnAzbW85N3ZsbmRvZmYifQ.9OWZ7YWGJ4DDIV4bCTwsaA';

function Map() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const mapLoaded = useUnit($mapLoaded);

  const setMapLoaded = useUnit(setMapLoadedEv);
  const resetMapLoaded = useUnit(resetMapLoadedEv);

  useEffect(() => {
    if (mapRef.current) return;
    window.map = new mapboxgl.Map({
      container: mapContainer.current,
      config: {
        basemap: {
          theme: 'monochrome',
        },
      },
      style: 'mapbox://styles/mapbox/standard',
      center: [0, 0],
      zoom: 1,
      projection: 'mercator',
      pitch: 45,
      bearing: -17.6,
      antialias: true,
    });
    mapRef.current = window.map;
    // window.map.dragRotate.disable();

    window.map.on('style.load', () => {
      mapRef.current.addLayer({
        id: 'add-3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 15,
        paint: {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'height'],
          ],
          'fill-extrusion-base': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'min_height'],
          ],
          'fill-extrusion-opacity': 0.6,
        },
      });
      setMapLoaded(true);
    });

    return () => {
      resetMapLoaded();
      delete window.map;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && !window.draw) {
      const Draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          point: true,
          trash: true,
        },
        styles: customStylesLight,
      });

      window.draw = Draw;

      const drawControls = new ExtendDrawBar({
        draw: Draw,
        buttons: [
          {
            on: 'click',
            action: deleteAll,
            classes: ['mapbox-gl-draw_ctrl-draw-btn', 'mapbox-gl-draw_trash'],
          },
        ],
      });

      const { modes } = MapboxDraw;
      // modes.simple_select.onDrag = function (state, e) {};

      if (window.map._controls.length != 3) {
        window.map.addControl(drawControls, 'bottom-left');
      }

      window.addEventListener('keyup', (e) => {
        if (window.draw.getMode() !== 'simple_select' && e.key === 'Escape') {
          window.draw.changeMode('simple_select');
        }
      });
      window.map.on('draw.modechange', modeChangeHandler);
      window.map.on('draw.create', createObject);
      window.map.on('draw.delete', deleteAll);
      window.map.on('draw.update', updatePolygon);
    }
  }, [mapLoaded]);

  return <div ref={mapContainer} className="map-container" />;
}

export default Map;
